// App.js

import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Route, Routes, Navigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider } from './theme/ThemeContext';
import { StyledSnackbarContent } from './theme/StyledSnackbar';
import CssBaseline from '@mui/material/CssBaseline';
import useMediaQuery from '@mui/material/useMediaQuery';
import Sidebar from './components/global/Sidebar';
import { SidebarProvider } from './components/global/SidebarContext';
import MessagePage from './components/global/MessagePage';
import { WorkspaceProvider, useWorkspace } from './contexts/WorkspaceContext';
import Header from './components/global/Header';
import PlanStatusBanner from './components/billing/PlanStatusBanner';
import AppBackdrop from './components/global/AppBackdrop';
import HomePage from './components/home/HomePage';
import DataSourcesPage from './components/datasources/DataSourcesPage';
import ConnectData from './components/datasources/ConnectData';
import EditDataSourcePage from './components/datasources/EditDataSourcePage';
import EditJobPage from './components/datasources/EditJobPage';
import AddSyncPage from './components/datasources/AddSyncPage';
import ConnectorDetailPage from './components/datasources/ConnectorDetailPage';
import CreateJob from './components/ingestion/CreateJob';
import UploadHistoryPage from './components/ingestion/UploadHistoryPage';
import SignalsPage from './components/signals/SignalsPage'
import RelationshipsPage from './components/signals/RelationshipsPage'
import WorkspacesPage from './components/workspaces/WorkspacesPage'
import WorkspaceCreatePage from './components/workspaces/WorkspaceCreate'
import EditWorkspacePage from './components/workspaces/EditWorkspacePage'
import WorkspaceDetailPage from './components/workspaces/WorkspaceDetailPage'
import SegmentsPage from './components/segment/SegmentsPage';
import SegmentStudio from './components/segment/SegmentStudio';
import EditSegmentPage from './components/segment/EditSegmentPage';
import SegmentDetailPage from './components/segment/SegmentDetailPage';
import BlueprintsPage from './components/blueprints/BlueprintsPage';
import BlueprintDetailPage from './components/blueprints/BlueprintDetailPage';
import CreateBlueprintPage from './components/blueprints/CreateBlueprintPage';
import UploadModelPage from './components/blueprints/UploadModelPage';
import PublicBlueprintsPage from './components/blueprints/PublicBlueprintsPage';
// Phase 13.O: the standalone foundation marketplace was retired in favor
// of /browse-public-blueprints (foundation backbones, custom, and BYOM
// behind a unified Type filter). Phase 14: foundation tuning no longer
// goes through the blueprints collection - the backbone detail page
// jumps straight to /pipelines/create?foundation=<uid>.
import TrainingsPage from './components/trainings/TrainingsPage';
import ModelsPage from './components/models/ModelsPage';
import ModelDetailPage from './components/models/ModelDetailPage';
import CreateModelPage from './components/models/CreateModelPage';
import EditModelPage from './components/models/EditModelPage';
import ForecastsPage from './components/forecasts/ForecastsPage';
import InferencesPage from './components/inferences/InferencesPage';
import InferenceDetailPage from './components/inferences/InferenceDetailPage';
import DeploymentsPage from './components/deployments/DeploymentsPage';
import DeploymentDetailPage from './components/deployments/DeploymentDetailPage';

// Placeholder pages for the upcoming prediction-graph sections. These are
// linked from the sidebar so the new navigation can be reviewed end-to-end;
// each route renders a "coming soon" page until its backend lands (see /plans).
import AnalystPage from './components/analyst/AnalystPage';
import AnalystDock, { AnalystDockProvider, ContentBody } from './components/analyst/AnalystDock';
import PlatformTourGuide from './components/tour/PlatformTourGuide';
import ComingSoonPage from './components/common/ComingSoonPage';
import NotFoundPage from './components/common/NotFoundPage';
import { mapLegacyPath } from './utils/notificationLink';
import DiscoveryPage from './components/discovery/DiscoveryPage';
import CreateGoalPage from './components/discovery/CreateGoalPage';
import GoalDetailPage from './components/discovery/GoalDetailPage';
import ForecastStudioPage from './components/discovery/ForecastStudioPage';
import ScenariosPage from './components/scenarios/ScenariosPage';
import DiscoveryInsightsPage from './components/discovery/DiscoveryInsightsPage';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

import "@fontsource/roboto-condensed";
import "@fontsource/roboto-condensed/300.css";
import "@fontsource/roboto-condensed/400.css";
import "@fontsource/roboto-condensed/500.css";
import "@fontsource/roboto-condensed/700.css";
import "@fontsource/kanit/400.css";
import '@fontsource-variable/lexend';

import { fetchRequest as getUser } from './redux/actions/user/getUser';
import { fetchRequest as fetchPublicSettings } from './redux/actions/settings/public';
import { fetchRequest as refreshToken } from './redux/actions/auth/refreshToken';

// Import auth components
import SetupPage from './components/auth/SetupPage';
import OrganizationInvitationPage from './components/auth/OrganizationInvitationPage';
import OpsSsoPage from './components/auth/OpsSsoPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import CreateAPITokenPage from './components/auth/CreateAPITokenPage';

// Data Signal Marketplace
import MarketplacePage from './components/marketplace/MarketplacePage';
import ListingDetailPage from './components/marketplace/ListingDetailPage';
import EditListingPage from './components/marketplace/EditListingPage';

// Settings shell + tabs (Profile, Notifications, ...)
import SettingsPage from './components/settings/SettingsPage';

// Full-page notifications history (linked from the bell drawer's
// "View all notifications" footer).
import NotificationsPage from './components/notifications/NotificationsPage';

import './App.css';
import './theme/themed.css'; // Import themed CSS variables
import Error from './utils/error';
import { isAuthenticated, getCurrentUser } from './utils/localUser';

// Phase 13.O: legacy /foundation/backbones/<version> URLs (the old
// dedicated PredictFM backbone detail page) now redirect to the
// unified model detail at /model/pfm__<version>.
function LegacyBackboneRedirect() {
  const { backboneVersion } = useParams();
  const target = backboneVersion
    ? `/model/pfm__${encodeURIComponent(backboneVersion)}`
    : '/browse-public-models';
  return <Navigate to={target} replace />;
}

function LegacyBlueprintDetailRedirect() {
  const { blueprintId } = useParams();
  return <Navigate to={`/model/${blueprintId}`} replace />;
}

// The public site links "Sign in" → /login and "Sign up" → /signup.
// A signed-in visitor has nothing to do on an auth page, so send them
// straight to the dashboard. Everyone else lands on the static auth
// page (login.html handles both sign-in and first-time sign-up via
// magic link / Google).
function AuthEntryRedirect() {
  if (isAuthenticated()) return <Navigate to="/" replace />;
  window.location.replace('/login.html');
  return null;
}

// Goals moved from /discovery/* to /goals/* — keep old goal links alive.
// Old notification links carried the target section as ?tab= (e.g.
// /discovery/<id>?tab=alerts); map it onto the /goals/:goalId/:section
// scheme instead of dropping it.
const GOAL_SECTION_SEGMENTS = new Set([
  'drivers', 'models', 'forecast', 'scenarios', 'serving',
  'alerts', 'activity', 'settings',
]);
function DiscoveryRedirect() {
  const { goalId } = useParams();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const section = tab && GOAL_SECTION_SEGMENTS.has(tab) ? `/${tab}` : '';
  return <Navigate to={`/goals/${goalId}${section}`} replace />;
}

// Notification rows + their emails deep-link with a workspace prefix
// (/workspaces/<uid>/<path>) so a click can land in the RIGHT workspace
// even when another one is active. Membership check first: switching is
// a hard navigation (same path the Header switcher uses) so every page
// remounts against the new workspace context.
function WorkspaceDeepLink() {
  const { workspaceId } = useParams();
  const location = useLocation();
  const { activeWorkspaceId, workspaces } = useWorkspace();

  const tail = location.pathname.replace(/^\/workspaces\/[^/]+/, '') || '/';
  const mapped = mapLegacyPath(tail + (location.search || ''));

  const isMember = workspaces.some((w) => w.uid === workspaceId);
  if (isMember && workspaceId !== activeWorkspaceId) {
    try { localStorage.setItem('WORKSPACE_ID', workspaceId); } catch (_e) { /* private mode */ }
    window.location.replace(mapped);
    return <MessagePage color="inherit" />;
  }
  // Already active (or no access — the target page surfaces its own
  // scoping error, which beats silently dropping the click).
  return <Navigate to={mapped} replace />;
}

// One redirect for every legacy path shape older notifications still
// store (they're persisted documents — the old links never go away).
function LegacyPathRedirect() {
  const location = useLocation();
  return <Navigate to={mapLegacyPath(location.pathname + location.search)} replace />;
}

// Billing moved into the unified Settings shell — old /billing[/x] and
// /credits links land on /settings/billing[/x].
function LegacyBillingRedirect() {
  const { section } = useParams();
  return (
    <Navigate
      to={section ? `/settings/billing/${section}` : '/settings/billing'}
      replace
    />
  );
}

// Monetization moved into Settings too; its old ?tab= query param maps
// onto the new sub-path (dashboard is the section root).
function LegacyMonetizationRedirect() {
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const target = tab && tab !== 'dashboard'
    ? `/settings/monetization/${tab}`
    : '/settings/monetization';
  return <Navigate to={target} replace />;
}

// Holds the app shell until WorkspaceProvider has resolved the active
// workspace, so workspace-scoped pages never mount + fetch before the
// active id is known. This is the ONE place selection is gated.
function WorkspaceGate({ children }) {
  const { ready } = useWorkspace();
  if (!ready) return <MessagePage color="inherit" />;
  return children;
}

// Phones land on the chat - it's the mobile home surface (the full
// dashboard doesn't earn its keep at 390px). Desktop and tablets keep
// the dashboard untouched.
function MobileHomeGate() {
  const isPhone = useMediaQuery('(max-width:599.95px)', { noSsr: true });
  return isPhone ? <Navigate to="/analyst" replace /> : <HomePage />;
}

function App({ getUser, fetchPublicSettings, refreshToken, isLoading, hasUser }) {
  const navigate = useNavigate();
  const [userDataFetched, setUserDataFetched] = React.useState(false);
  const [authInitialized, setAuthInitialized] = React.useState(false);

  useEffect(() => {
    // Initialize auth state from localStorage on app startup
    const token = localStorage.getItem('authToken');
    const userStr = localStorage.getItem('user');
    
    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        // Initialize Redux auth state if we have stored auth data
        // This could dispatch an action to set initial auth state
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('user');
      }
    }
    
    setAuthInitialized(true);
  }, []);

  useEffect(() => {
    if (!authInitialized) return;

    // Check for authentication using new auth system
    const authenticated = isAuthenticated();
    const currentUser = getCurrentUser();

    if (authenticated && !userDataFetched) {
      // If user is authenticated, fetch user data
      getUser();
      // Pull app-wide public settings (credit USD value, platform
      // fee) once at sign-in. Cached for the rest of the session.
      fetchPublicSettings();
      // Slide the session forward on every app open: swap the stored JWT for
      // a fresh one so the expiry window restarts from now. The backend caps
      // total session age via the `sst` claim
      // (auth.jwt_session_absolute_max_days), at which point the refresh
      // returns 401 and the global error handler routes back to login.
      refreshToken();
      setUserDataFetched(true);
    } else if (!authenticated) {
      // Only redirect to login if we're not already on an auth page.
      // Invitation links and the ops-console handoff must render
      // logged-out (they stash their params and hand off to login
      // themselves, returning here after sign-in).
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/onboarding')
          && !currentPath.includes('/organization-invitation')
          && !currentPath.includes('/sso/ops')) {
        // Redirect to static login page
        window.location.href = '/login.html';
      }
    }
  }, [getUser, fetchPublicSettings, refreshToken, navigate, userDataFetched, authInitialized]);

  // Workspace selection + reconciliation now lives entirely in
  // WorkspaceProvider (the single source of truth). The protected shell is
  // wrapped in <WorkspaceProvider> + <WorkspaceGate> below, so workspace-scoped
  // pages only mount once the active workspace is resolved.

  // Show the splash only while the signed-in user's data is being fetched
  // for the FIRST time. user_getUser.isLoading INITIALIZES to true and only
  // flips once a getUser action lands — for a logged-out visitor that
  // never happens, so gating on it alone would strand the auth pages
  // that must render logged-out (/sso/ops, /organization-invitation)
  // on an eternal splash screen. Once a payload exists, background
  // refetches (e.g. after creating the first workspace during setup)
  // must never blank the app.
  if (isLoading && !hasUser && isAuthenticated()) {
    return <MessagePage color="inherit" />;
  }

  return (
    <ThemeProvider>
      <CssBaseline />
      <SnackbarProvider
        maxSnack={3}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        Components={{
          default: StyledSnackbarContent,
          success: StyledSnackbarContent,
          error: StyledSnackbarContent,
          warning: StyledSnackbarContent,
          info: StyledSnackbarContent,
        }}
      >
        <div className="App">
          <Error />
          <Routes>
            {/* Auth routes - chat-first workspace setup, outside the shell */}
            <Route path="/onboarding" element={<SetupPage />} />

            {/* Auth entry points the public site links to. Signed-in
                users are already past these pages — take them to the
                dashboard instead of a dead-end auth screen. */}
            <Route path="/login" element={<AuthEntryRedirect />} />
            <Route path="/signup" element={<AuthEntryRedirect />} />

            {/* Organization-invitation emails land here (works
                logged-out; hands off to login and returns). */}
            <Route path="/organization-invitation/:token" element={<OrganizationInvitationPage />} />

            {/* Ops-console SSO authorization (works logged-out; hands
                off to login and returns to mint the one-time code). */}
            <Route path="/sso/ops" element={<OpsSsoPage />} />

            {/* Main app routes with sidebar and header */}
            <Route path="/*" element={
              <WorkspaceProvider>
              <WorkspaceGate>
              <SidebarProvider>
              <AnalystDockProvider>
              <div style={{ display: 'flex' }}>
                <Sidebar />
                <div className="right-section">
                  <Header />
                  {/* ContentBody = .content-body + right padding that
                      makes room for the docked Analyst sidecar. */}
                  <ContentBody>
                    <AppBackdrop />
                    {/* App-wide plan lifecycle notice (expired / ending
                        soon / suspended) — reads the same server verdict
                        the API enforces. Renders nothing when healthy. */}
                    <PlanStatusBanner />
                    <Routes>
                      {/* Protected routes */}
                      <Route path="/" element={<ProtectedRoute><MobileHomeGate /></ProtectedRoute>} />
                <Route path="/workspaces" element={<ProtectedRoute><WorkspacesPage /></ProtectedRoute>} />
                {/* Workspace-prefixed deep links from notifications and
                    emails: switch workspace if needed, then open the
                    inner path (legacy shapes included). */}
                <Route path="/workspaces/:workspaceId/*" element={<ProtectedRoute><WorkspaceDeepLink /></ProtectedRoute>} />
                <Route path="/new-workspace" element={<ProtectedRoute><WorkspaceCreatePage /></ProtectedRoute>} />
                <Route path="/workspace/:workspaceId" element={<ProtectedRoute><WorkspaceDetailPage /></ProtectedRoute>} />
                <Route path="/workspace/:workspaceId/edit" element={<ProtectedRoute><EditWorkspacePage /></ProtectedRoute>} />

                <Route path="/datasources" element={<ProtectedRoute><DataSourcesPage /></ProtectedRoute>} />
                <Route path="/datasources/connect" element={<ProtectedRoute><ConnectData /></ProtectedRoute>} />
                {/* Single unified create flow - old "classic" route redirects in. */}
                <Route path="/datasources/create" element={<Navigate to="/datasources/connect" replace />} />
                <Route path="/datasources/new" element={<Navigate to="/datasources/connect" replace />} />
                <Route path="/datasources/uploads" element={<ProtectedRoute><UploadHistoryPage /></ProtectedRoute>} />
                <Route path="/datasources/edit/:sourceId" element={<ProtectedRoute><EditDataSourcePage /></ProtectedRoute>} />
                <Route path="/datasources/:sourceId" element={<ProtectedRoute><ConnectorDetailPage /></ProtectedRoute>} />
                <Route path="/datasources/:sourceId/jobs/add" element={<ProtectedRoute><AddSyncPage /></ProtectedRoute>} />
                <Route path="/datasources/:sourceId/jobs/create" element={<ProtectedRoute><CreateJob /></ProtectedRoute>} />
                <Route path="/datasources/:sourceId/jobs/:jobId/edit" element={<ProtectedRoute><EditJobPage /></ProtectedRoute>} />
                <Route path="/signals" element={<ProtectedRoute><SignalsPage /></ProtectedRoute>} />

                {/* ── Intelligence ── */}
                {/* Discovery hosts the AI insights feed (formerly the
                    "Insights" tab on Data Signals). */}
                <Route path="/discovery" element={<ProtectedRoute><DiscoveryInsightsPage /></ProtectedRoute>} />
                <Route path="/relationships" element={<ProtectedRoute><RelationshipsPage /></ProtectedRoute>} />
                <Route path="/analyst" element={<ProtectedRoute><AnalystPage /></ProtectedRoute>} />

                {/* ── Actions ── */}
                {/* Goals — the discovery engine's outcome-first surface:
                    declare a target, the funnel finds + validates drivers. */}
                <Route path="/goals" element={<ProtectedRoute><DiscoveryPage /></ProtectedRoute>} />
                <Route path="/goals/create" element={<ProtectedRoute><CreateGoalPage /></ProtectedRoute>} />
                {/* Fleet creation merged into the single create form (scope
                    toggle); old bookmarks land on the form pre-set to fleet. */}
                <Route path="/goals/create-fleet" element={<Navigate to="/goals/create?kind=fleet" replace />} />
                <Route path="/goals/:goalId" element={<ProtectedRoute><GoalDetailPage /></ProtectedRoute>} />
                {/* Goal-scope sections (rail 2): Overview is the index above; the
                    rest render inside the same goal layout via the :section param. */}
                <Route path="/goals/:goalId/:section" element={<ProtectedRoute><GoalDetailPage /></ProtectedRoute>} />
                {/* Old goal bookmarks / notification links still say /discovery. */}
                <Route path="/discovery/create" element={<Navigate to="/goals/create" replace />} />
                <Route path="/discovery/:goalId" element={<DiscoveryRedirect />} />
                <Route path="/forecast-studio" element={<ProtectedRoute><ForecastStudioPage /></ProtectedRoute>} />
                <Route path="/scenarios" element={<ProtectedRoute><ScenariosPage /></ProtectedRoute>} />
                <Route path="/alerts" element={<ProtectedRoute><ComingSoonPage
                  title="Alerts"
                  icon={<NotificationsActiveIcon />}
                  description="Get notified when a forecast, regime change, or data-health signal crosses your thresholds."
                  phase="Roadmap · Phase 15"
                /></ProtectedRoute>} />

                {/* <Route path="/add-datasource" element={<AddDataSourcePage />} /> */}
                {/* <Route path="/configure-datasource/:id" element={<ConfigureDataSource />} /> */}
                <Route path="/segments" element={<ProtectedRoute><SegmentsPage /></ProtectedRoute>} />
                <Route path="/segments/create" element={<ProtectedRoute><SegmentStudio /></ProtectedRoute>} />
                {/* The retired classic creator's URL redirects into the studio. */}
                <Route path="/segments/create/classic" element={<Navigate to="/segments/create" replace />} />
                <Route path="/segments/edit/:segmentId" element={<ProtectedRoute><EditSegmentPage /></ProtectedRoute>} />
                <Route path="/segments/:segmentId" element={<ProtectedRoute><SegmentDetailPage /></ProtectedRoute>} />
                <Route path="/models" element={<ProtectedRoute><BlueprintsPage /></ProtectedRoute>} />
                <Route path="/models/create" element={<ProtectedRoute><CreateBlueprintPage /></ProtectedRoute>} />
                <Route path="/models/upload" element={<ProtectedRoute><UploadModelPage /></ProtectedRoute>} />
                <Route path="/model/:blueprintId" element={<ProtectedRoute><BlueprintDetailPage /></ProtectedRoute>} />
                <Route path="/browse-public-models" element={<ProtectedRoute><PublicBlueprintsPage /></ProtectedRoute>} />
                {/* Legacy model (blueprint) URLs */}
                <Route path="/blueprints" element={<Navigate to="/models" replace />} />
                <Route path="/blueprints/create" element={<Navigate to="/models/create" replace />} />
                <Route path="/blueprint/:blueprintId" element={<LegacyBlueprintDetailRedirect />} />
                <Route path="/browse-public-blueprints" element={<Navigate to="/browse-public-models" replace />} />

                {/* Phase 13.O: legacy foundation-marketplace URLs all
                    redirect to the unified /browse-public-blueprints
                    page (with the appropriate Type filter pre-selected
                    via query string). */}
                <Route path="/foundation/marketplace" element={<Navigate to="/browse-public-models" replace />} />
                <Route path="/foundation/leaderboard" element={<Navigate to="/browse-public-models?kind=foundation_backbone&sort_by=rating" replace />} />
                <Route path="/foundation/external" element={<Navigate to="/browse-public-models?kind=foundation_backbone" replace />} />
                <Route path="/foundation/external/:family" element={<Navigate to="/browse-public-models?kind=foundation_backbone" replace />} />
                {/* Old per-backbone detail URLs map onto the unified
                    blueprint detail page via the synthesized pfm__/extfm__
                    prefix scheme (see core/src/api/blueprint/foundation_views.py). */}
                <Route path="/foundation/backbones/:backboneVersion"
                       element={<ProtectedRoute><LegacyBackboneRedirect /></ProtectedRoute>} />

                {/* Billing lives inside the unified Settings shell now.
                    Legacy /credits and /billing[/section] links redirect
                    so bookmarks and in-app deep links keep working. */}
                <Route path="/credits" element={<ProtectedRoute><LegacyBillingRedirect /></ProtectedRoute>} />
                <Route path="/billing" element={<ProtectedRoute><LegacyBillingRedirect /></ProtectedRoute>} />
                <Route path="/billing/:section" element={<ProtectedRoute><LegacyBillingRedirect /></ProtectedRoute>} />
                {/* Phase 14: ``/blueprint/create-external`` retired -
                    foundation tuning no longer creates a blueprint. The
                    "Fine tune" CTA on a backbone detail page now jumps
                    straight to /pipelines/create?foundation=<uid>. */}
                {/* Phase 13.O: Pipelines (the workspace-scoped, scheduled
                    trainer that lives in the `models` Mongo collection).
                    /pipelines* is the canonical URL; /models* and
                    /model/<id>* are kept as aliases so existing links
                    + bookmarks don't break. */}
                <Route path="/pipelines" element={<ProtectedRoute><ModelsPage /></ProtectedRoute>} />
                <Route path="/pipelines/create" element={<ProtectedRoute><CreateModelPage /></ProtectedRoute>} />
                <Route path="/pipeline/:modelId" element={<ProtectedRoute><ModelDetailPage /></ProtectedRoute>} />
                <Route path="/pipeline/:modelId/edit" element={<ProtectedRoute><EditModelPage /></ProtectedRoute>} />
                {/* Legacy aliases - same components, kept indefinitely
                    so external links into /models or /model/<id> keep
                    working. New navigation should use /pipelines*. */}
                <Route path="/models" element={<ProtectedRoute><ModelsPage /></ProtectedRoute>} />
                <Route path="/models/create" element={<ProtectedRoute><CreateModelPage /></ProtectedRoute>} />
                <Route path="/model/:modelId" element={<ProtectedRoute><ModelDetailPage /></ProtectedRoute>} />
                <Route path="/model/:modelId/edit" element={<ProtectedRoute><EditModelPage /></ProtectedRoute>} />
                <Route path="/trainings" element={<ProtectedRoute><TrainingsPage /></ProtectedRoute>} />
                <Route path="/deployments" element={<ProtectedRoute><DeploymentsPage /></ProtectedRoute>} />
                <Route path="/deployment/:promotionId" element={<ProtectedRoute><DeploymentDetailPage /></ProtectedRoute>} />

                {/* Legacy path shapes stored in old notification rows +
                    emails (see utils/notificationLink.js). Notifications
                    are persisted documents, so these links never expire. */}
                <Route path="/dashboard" element={<LegacyPathRedirect />} />
                <Route path="/data-sources" element={<LegacyPathRedirect />} />
                <Route path="/data-sources/*" element={<LegacyPathRedirect />} />
                <Route path="/data-signals" element={<LegacyPathRedirect />} />
                <Route path="/trainings/:trainingId" element={<LegacyPathRedirect />} />
                <Route path="/deployments/:promotionId" element={<LegacyPathRedirect />} />
                <Route path="/foundation/:modelId" element={<LegacyPathRedirect />} />
                <Route path="/inference" element={<ProtectedRoute><InferencesPage /></ProtectedRoute>} />
                <Route path="/inferences/:uid" element={<ProtectedRoute><InferenceDetailPage /></ProtectedRoute>} />
                {/* Legacy alias kept for any old links/bookmarks */}
                <Route path="/forecasts-legacy" element={<ProtectedRoute><ForecastsPage /></ProtectedRoute>} />
                      {/* API tokens live under Settings › API Keys; the
                          full-page create wizard stays standalone. */}
                      <Route path="/api-tokens" element={<Navigate to="/settings/api-keys" replace />} />
                      <Route path="/api-tokens/new" element={<ProtectedRoute><CreateAPITokenPage /></ProtectedRoute>} />

                      {/* Data Signal Marketplace */}
                      <Route path="/marketplace" element={<ProtectedRoute><MarketplacePage /></ProtectedRoute>} />
                      {/* Legacy /marketplace/manage redirects to the
                          new Monetization surface in the user menu. */}
                      <Route path="/marketplace/manage" element={<Navigate to="/monetization" replace />} />
                      <Route path="/marketplace/listings/:listingId" element={<ProtectedRoute><ListingDetailPage /></ProtectedRoute>} />
                      {/* Standalone full-page edit for an existing
                          listing — no longer part of the publish wizard
                          (the wizard is create-only). */}
                      <Route path="/marketplace/listings/:listingId/edit" element={<ProtectedRoute><EditListingPage /></ProtectedRoute>} />
                      <Route path="/monetization" element={<ProtectedRoute><LegacyMonetizationRedirect /></ProtectedRoute>} />

                      {/* Unified Settings shell — Account, Billing,
                          Monetization and Organization groups behind one
                          rail. ``/settings`` lands on Profile;
                          ``/settings/billing/usage`` etc. deep-link into
                          grouped sub-sections. */}
                      <Route path="/settings" element={<Navigate to="/settings/profile" replace />} />
                      <Route path="/settings/:section" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                      <Route path="/settings/:section/:sub" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />

                      {/* Full notifications history with pagination +
                          unread-only filter + mark-all-read. Linked
                          from the bell drawer's "View all" footer. */}
                      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />

                      {/* Anything unmatched renders a proper 404 inside
                          the shell instead of a blank content area. */}
                      <Route path="*" element={<ProtectedRoute><NotFoundPage /></ProtectedRoute>} />
                    </Routes>
                  </ContentBody>
                </div>
                {/* The Analyst sidecar: docked panel / floating pill on
                    every page except /analyst (which owns the full view). */}
                <AnalystDock />
                {/* Second act of the guided tour: the workspace-page walk
                    (Signals -> ... -> Data Sources). App-level so the
                    coach card survives navigation between pages. */}
                <PlatformTourGuide />
              </div>
              </AnalystDockProvider>
              </SidebarProvider>
              </WorkspaceGate>
              </WorkspaceProvider>
            } />
          </Routes>
        </div>
        <SnackbarHandler />
      </SnackbarProvider>
    </ThemeProvider>
  );
}

function SnackbarHandler() {
  const { enqueueSnackbar } = useSnackbar();
  const { text: successText, timestamp: successTimestamp } = useSelector(state => state.snackbar.successMessage);
  const { text: errorText, timestamp: errorTimestamp } = useSelector(state => state.snackbar.errorMessage);

  useEffect(() => {
    if (successTimestamp) {
      enqueueSnackbar(successText, { variant: 'success' });
    }
  }, [successText, successTimestamp, enqueueSnackbar]);

  useEffect(() => {
    if (errorTimestamp) {
      enqueueSnackbar(errorText, { variant: 'error' });
    }
  }, [errorText, errorTimestamp, enqueueSnackbar]);

  return null;
}

const mapStateToProps = (state) => {
  return {
    isLoading: state.user_getUser.isLoading,
    // The slice initialises response to {} - only a non-empty payload
    // counts as "user data present".
    hasUser: Object.keys(state.user_getUser.response || {}).length > 0,
  };
}

const mapDispatchToProps = (dispatch) => ({
  getUser: () => dispatch(getUser()),
  fetchPublicSettings: () => dispatch(fetchPublicSettings()),
  refreshToken: () => dispatch(refreshToken()),
});

export default connect(mapStateToProps, mapDispatchToProps)(App);
